// Component for SideBar Settings
import React, { useEffect } from 'react';

import { Button, Grid, MenuItem, Paper, TextField, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { theme } from '../../theme/theme';

import { usePostMetadataQuery, usePostGetDeviceSettingsCmdQuery, usePostUpdateDeviceSettingsCmdQuery } from "./apiCloudSystem";

import OperatingTimezoneSelect from './inputs/OperatingTimezoneSelect';
import SoftwareUpdFrequencySelect from './inputs/SoftwareUpdFrequencySelect';
import ContentLoadSelect from './inputs/ContentLoadSelect';
import OperatingRegionSelect from "./inputs/OperatingRegionSelect";
import WiFiFrequencySelect from "./inputs/WiFiFrequencySelect";
import StatusWiFiChannelSelect from "./inputs/StatusWiFiChannelSelect";
import CountrySelect from "./inputs/CountrySelect";
import StateSelect from "./inputs/StateSelect";

import airports from "../../data/airports.json";

// JS doesn't have interfaces
//  use this commented out definition as a reference for the props that should be accepted for this component 
// export interface SideBarSettingsProps {
//   cloudSystemDetails : CloudSystem
//   onNeedsGet : func
//   props.waitingOnSettings : bool (Get-settings or Change-settings is in progress)
// }

const getAirportDetailsFromCode = (airport_id) => {
  var details = {
    latitude: null,
    longitude: null,
    name: null,
    region: null
  };

  if (airport_id === null || airport_id === undefined) {
    return null;
  }

  // Get details from airports.json by airport code
  var allAirports = Object.entries(airports);
  var airport = allAirports?.find(
    ([key, ]) => { 
      return key === airport_id; 
    });

  if (airport) {
    var [, value] = airport;
    details.latitude = value.latitude;
    details.longitude = value.longitude;
    details.name = value.name;
    details.region = value.region;
  }

  return details;
}

const SideBarSettings = (props) => {

  const defaultGeneralSettings = {
    timezone: undefined,
    updateFrequency: undefined,
    loadTime: undefined,
    wifiRegion: undefined,
    wifiFrequency: '2.4',
    wifiChannel: undefined,
    wifiSSID: undefined,
    install_type:'region',
    install_country: undefined,
    install_state: undefined,
    install_city: undefined,
    airport_id: undefined,
  };

  // Metadata fields
  const [customer, setCustomer] = React.useState(props.cloudSystemDetails.customer);
  const [shortName, setShortName] = React.useState(props.cloudSystemDetails.shortName);
  const [expectedAPCount, setExpectedAPCount] = React.useState(props.cloudSystemDetails.expectedAPCount);

  // General Settings fields
  const cloudSystemSettings = Object.fromEntries(props.cloudSystemDetails?.settings?.map(kvPair => [kvPair.key, kvPair.value]) ?? []);
  const initialSettings = Object.keys(cloudSystemSettings).length !== 0 ? cloudSystemSettings : defaultGeneralSettings;

  const [generalSettings, setGeneralSettings] = React.useState(initialSettings);
  
  const initialAirportDetails = getAirportDetailsFromCode(props.currentSettingsInputs?.airport_id ?? cloudSystemSettings?.airport_id);
  const [airportDetails, setAirportDetails] = React.useState(initialAirportDetails); // contains Latitude, Longitude, Name, Region
  const [isAirportCodeValid, setIsAirportCodeValid] = React.useState(false);

  // API triggers
  const [metadataForUpdate, setMetadataForUpdate] = React.useState(null);
  const [needsUpdateMetadata, setNeedsUpdateMetadata] = React.useState(false);
  const [serialForPullSettings, setSerialForPullSettings] = React.useState(null);
  const [needsPullSettings, setNeedsPullSettings] = React.useState(false);
  const [settingsForUpdate, setSettingsForUpdate] = React.useState(null);
  const [needsUpdateSettings, setNeedsUpdateSettings] = React.useState(false);

  const {
    data: didUpdateMetadata,
    isError: isUpdateError,
    error: updateError,
  } = usePostMetadataQuery(metadataForUpdate, { refetchOnMountOrArgChange: true, skip: !needsUpdateMetadata });

  const {
    data: didUpdateSettings,
    isError: isUpdateSettingsError,
    error: updateSettingsError,
  } = usePostUpdateDeviceSettingsCmdQuery(settingsForUpdate, { refetchOnMountOrArgChange: true, skip: !needsUpdateSettings });

  const {
    data: didPostGetSettings,
    isError: isPullSettingsError,
    error: pullSettingsError,
  } = usePostGetDeviceSettingsCmdQuery(serialForPullSettings, { refetchOnMountOrArgChange: true, skip: !needsPullSettings });

  React.useEffect(() => {
    if (isUpdateError) {
      console.error(`Error updating metadata: ${updateError}`);
    }
  }, [isUpdateError, updateError]);

  React.useEffect(() => {
    if (isUpdateSettingsError) {
      console.error(`Error sending ChangeSettings command: ${updateSettingsError}`);
    }
  }, [isUpdateSettingsError, updateSettingsError]);

  React.useEffect(() => {
    if (isPullSettingsError) {
      console.error(`Error sending PullSettings command: ${pullSettingsError}`);
    }
  }, [isPullSettingsError, pullSettingsError]);

  // Reload values from fresh Get
  React.useEffect(() => {

    const newSettings = Object.fromEntries(props.cloudSystemDetails?.settings?.map(kvPair => [kvPair.key, kvPair.value]) ?? []);   

    if(JSON.stringify(newSettings) === JSON.stringify(generalSettings))
    {
      console.log("No difference in settings, skipping update!")
      return
    }
    else {
      console.log("Settings changed in the background, clearing user inputs")
      props.setCurrentSettingsInputs({})
    }

    // Metadata
    if (props.cloudSystemDetails?.customer !== undefined && props.cloudSystemDetails.customer !== customer) {
      setCustomer(props.cloudSystemDetails.customer)
    }
    if (props.cloudSystemDetails?.shortName !== undefined && props.cloudSystemDetails.shortName !== shortName) {
      setShortName(props.cloudSystemDetails.shortName)
    }
    if (props.cloudSystemDetails?.expectedAPCount !== undefined && props.cloudSystemDetails.expectedAPCount !== expectedAPCount) {
      setExpectedAPCount(props.cloudSystemDetails.expectedAPCount)
    }

    var updatedSettings = Object.assign({}, generalSettings);

    if (newSettings?.timezone !== undefined && newSettings.timezone !== generalSettings.timezone) {
      updatedSettings.timezone = newSettings.timezone;
    }
    if (newSettings?.updateFrequency !== undefined && newSettings.updateFrequency !== generalSettings.updateFrequency) {
      updatedSettings.updateFrequency = newSettings.updateFrequency;
    }
    if (newSettings?.loadTime !== undefined && newSettings.loadTime !== generalSettings.loadTime) {
      updatedSettings.loadTime = newSettings.loadTime;
    }
    if (newSettings?.wifiRegion !== undefined && newSettings.wifiRegion !== generalSettings.wifiRegion) {
      updatedSettings.wifiRegion = newSettings.wifiRegion;
    }
    if (newSettings?.wifiFrequency !== undefined && newSettings.wifiFrequency !== generalSettings.wifiFrequency) {
      updatedSettings.wifiFrequency = newSettings.wifiFrequency;
    }
    if (newSettings?.wifiChannel !== undefined && newSettings.wifiChannel !== generalSettings.wifiChannel) {
      updatedSettings.wifiChannel = newSettings.wifiChannel;
    }
    if (newSettings?.wifiSSID !== undefined && newSettings.wifiSSID !== generalSettings.wifiSSID) {
      updatedSettings.wifiSSID = newSettings.wifiSSID;
    }
    if (newSettings?.install_country !== undefined && newSettings.install_country !== generalSettings.install_country) {
      updatedSettings.install_country = newSettings.install_country;
    }
    if (newSettings?.install_state !== undefined && newSettings.install_state !== generalSettings.install_state) {
      updatedSettings.install_state = newSettings.install_state;
    }
    if (newSettings?.install_city !== undefined && newSettings.install_city !== generalSettings.install_city) {
      updatedSettings.install_city = newSettings.install_city;
    }
    if (newSettings?.airport_id !== undefined && newSettings.airport_id !== generalSettings.airport_id) {
      updatedSettings.airport_id = newSettings.airport_id;
    }
    if (newSettings?.install_type !== undefined && newSettings.install_type !== generalSettings.install_type) {
      updatedSettings.install_type = newSettings.install_type;
    }
      
    setGeneralSettings(updatedSettings);

  }, [props.cloudSystemDetails?.settings,props.cloudSystemDetails.actions?.find(x => x.method === 'get-settings' && x.status === 'Finished')?.updatedAt,props.cloudSystemDetails.actions?.find(x => x.method === 'change-settings' && x.status === 'Finished')?.updatedAt]);

  React.useEffect(() => {
    if (didUpdateMetadata || didUpdateSettings || didPostGetSettings) {
      // Trigger GET immediately
      if (props.onNeedsGet) {
        props.onNeedsGet()
      }
    }
  }, [didUpdateMetadata, didUpdateSettings, didPostGetSettings]);

  const handlePullFromDeviceClicked = () => {
    if (props.cloudSystemDetails !== null && props.cloudSystemDetails !== undefined) {
      var forPullSettings = {
        serialNumber: props.cloudSystemDetails.serialNumber,
        trigger: serialForPullSettings?.trigger === 1 ? 2 : 1 // Changing part of the object passed to the Redux hook triggers the query for subsequent calls
      };

      setSerialForPullSettings(forPullSettings);
      setNeedsPullSettings(true);
    } else {
      console.error("Error setting CloudSystem serial to get settings");
    }
  };

  const handleApplyChanges = () => {
    var forUpdate = {
      "serialNumber": props.cloudSystemDetails.serialNumber,
      "shortName": shortName !== props.cloudSystemDetails.shortName ? shortName : undefined,
      "customer": customer !== props.cloudSystemDetails.customer ? customer : undefined,
      "expectedAPCount": isHSPrefix ? undefined : (expectedAPCount !== props.cloudSystemDetails.expectedAPCount ? expectedAPCount : undefined),
      "isCommissioned": true,
    };

    setMetadataForUpdate(forUpdate);
    setNeedsUpdateMetadata(true);

    if (props.cloudSystemDetails.isDashboardEnabled) {
      // Also handle change-settings if the cloud system supports actions
      var forChangeSettings = {
        "serialNumber": props.cloudSystemDetails.serialNumber,
        "params": {
          "timezone": generalSettings.timezone !== cloudSystemSettings?.timezone ? generalSettings.timezone : undefined,
          "updateFrequency": generalSettings.updateFrequency !== cloudSystemSettings?.updateFrequency ? generalSettings.updateFrequency : undefined,
          "loadTime": generalSettings.loadTime !== cloudSystemSettings?.loadTime ? generalSettings.loadTime : undefined,
          "wifiRegion": generalSettings.wifiRegion !== cloudSystemSettings?.wifiRegion ? generalSettings.wifiRegion : undefined,
          "wifiFrequency": generalSettings.wifiFrequency !== cloudSystemSettings?.wifiFrequency ? generalSettings.wifiFrequency : undefined,
          "wifiChannel": generalSettings.wifiChannel !== cloudSystemSettings?.wifiChannel ? generalSettings.wifiChannel : undefined,
          "wifiSSID": generalSettings.wifiSSID !== cloudSystemSettings?.wifiSSID ? generalSettings.wifiSSID : undefined,
          "install_type": generalSettings.install_type !== cloudSystemSettings?.install_type ? generalSettings.install_type : undefined,
          "install_country": generalSettings.install_country !== cloudSystemSettings?.install_country ? generalSettings.install_country : undefined,
          "install_state": generalSettings.install_state !== cloudSystemSettings?.install_state ? generalSettings.install_state : undefined,
          "install_city": generalSettings.install_city !== cloudSystemSettings?.install_city ? generalSettings.install_city : undefined,
          "airport_id": generalSettings.airport_id !== cloudSystemSettings?.airport_id && isAirportCodeValid ? generalSettings.airport_id.toUpperCase() : undefined,
        }
      };

      var needsChangeSettings = 
        forChangeSettings.params.timezone !== undefined || 
        forChangeSettings.params.updateFrequency !== undefined || 
        forChangeSettings.params.loadTime !== undefined || 
        forChangeSettings.params.wifiRegion !== undefined ||
        forChangeSettings.params.wifiFrequency !== undefined ||
        forChangeSettings.params.wifiChannel !== undefined ||
        forChangeSettings.params.wifiSSID !== undefined ||
        forChangeSettings.params.install_country !== undefined ||
        forChangeSettings.params.install_state !== undefined ||
        forChangeSettings.params.install_type !== undefined ||
        forChangeSettings.params.install_city !== undefined ||
        forChangeSettings.params.airport_id !== undefined;

      setSettingsForUpdate(forChangeSettings);
      setNeedsUpdateSettings(needsChangeSettings);
    }
  };

  const handleCustomerChanged = (event) => {
    setCustomer(event.target.value);
  };

  const handleShortNameChanged = (event) => {
    setShortName(event.target.value);
  };

  const handleExpectedAPCountChanged = (event) => {
    setExpectedAPCount(event.target.value);
  };

  const handleGeneralSettingsUpdated = (keyValuePair) => {
    const settings = {};
    Object.assign(settings, generalSettings, keyValuePair);
    setGeneralSettings(settings);

    const userInput = {};
    Object.assign(userInput, props.currentSettingsInputs, keyValuePair);
    props.setCurrentSettingsInputs(userInput)

  };

  const handleLocationTypeChanged = (event) => {    
    switch(event.target.value) {
      case "region" :
        handleGeneralSettingsUpdated({"install_type":event.target.value,"airport_id":""});
        break;
      case "airport":
        handleGeneralSettingsUpdated({"install_type":event.target.value,"install_city":"","install_state":"","install_country":""});
        break;
      default:
        handleGeneralSettingsUpdated({"install_type":event.target.value});
        break;
    }
  };

  const handleCityChanged = (event) => {
    handleGeneralSettingsUpdated({"install_city": event.target.value});
  };

  const handleWifiSSIDChanged = (event) => {
    handleGeneralSettingsUpdated({"wifiSSID": event.target.value});
  };

  const handleAirportCodeChanged = (event) => {
    handleGeneralSettingsUpdated({"airport_id": event.target.value});
  };

  useEffect(() => {
    // Recalculate location fields from airport code
    var details = getAirportDetailsFromCode(generalSettings.airport_id?.toUpperCase());
    setIsAirportCodeValid(details !== null && details !== undefined);
    setAirportDetails(details);
  }, [generalSettings.airport_id]);


  const isHSPrefix = props.cloudSystemDetails?.serialNumber?.startsWith("HS-") ?? false;
 
  return (
    <Paper
        elevation={0}
        sx={{
            p: 2,
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: theme.palette.primary.main
        }}
    >
      <Grid container>
        <Grid item>
          <Typography fontWeight={700} sx={{ color: theme.palette.primary.contrastText }}>
              Device Settings
          </Typography>
        </Grid>
        <Grid item sx={{ marginLeft: "auto" }}>
          {props.waitingOnSettings && (
            <CircularProgress size="1rem" sx={{ color: theme.palette.tertiary.main, mr: 1 }}/>
          )}
          {props.cloudSystemDetails.isDashboardEnabled && (
            <Button onClick={handlePullFromDeviceClicked} disabled={props.waitingOnSettings} sx={{ color: theme.palette.tertiary.contrastText, backgroundColor: theme.palette.tertiary.main }}>
                Pull from Device
            </Button>
          )}
        </Grid>
        <Grid item sx={{ marginLeft: 1 }}>
          <Button onClick={handleApplyChanges} disabled={props.waitingOnSettings} sx={{ color: theme.palette.tertiary.contrastText, backgroundColor: theme.palette.tertiary.main }}>
              Apply
          </Button>
        </Grid>
      </Grid>
      <Grid container sx={{ marginTop: 2 }}>
        <Grid item xs={6} sx={{ my: 1 }}>
          <Typography sx={{ color: theme.palette.sidebarContent.labelText }}>
              Serial Number
          </Typography>
        </Grid>
        <Typography sx={{ color: theme.palette.sidebarContent.valueText, marginLeft: 'auto', my: 1 }}>
            {props.cloudSystemDetails.serialNumber}
        </Typography>
        <Grid container direction="row" alignItems={'center'}>
          <Grid item>
            <Typography sx={{ color: theme.palette.sidebarContent.labelText }}>
                Customer
            </Typography>
          </Grid>
          <Grid item xs={6} sx={{ marginLeft: 'auto' }}>
            {/* Input  */}
            <TextField 
              onChange={handleCustomerChanged}
              disabled={props.waitingOnSettings}
              value={customer}
              fullWidth
              sx={{ 
                "& .MuiOutlinedInput-root":{"& > fieldset": {borderColor: theme.palette.secondary.main}},
                "& .MuiOutlinedInput-input.Mui-disabled":{WebkitTextFillColor: 'white'},
                input: { 
                  color: theme.palette.sidebarContent.valueText 
                },
                textAlignLast: 'end'
              }}
            />
          </Grid>
        </Grid>
        <Grid container direction='row' alignItems='center' sx={{ mt: 1 }}>
          <Grid item>
            <Typography sx={{ color: theme.palette.sidebarContent.labelText }}>
                Short Name
            </Typography>
          </Grid>
          <Grid item xs={6} sx={{ marginLeft: 'auto' }}>
            {/* Input  */}
            <TextField 
              onChange={handleShortNameChanged}
              disabled={props.waitingOnSettings}
              value={shortName}
              fullWidth
              sx={{ 
                "& .MuiOutlinedInput-root":{"& > fieldset": {borderColor: theme.palette.secondary.main}},
                "& .MuiOutlinedInput-input.Mui-disabled":{WebkitTextFillColor: 'white'},
                input: { 
                  color: theme.palette.sidebarContent.valueText, 
                }, 
                textAlignLast: 'end'
              }}
            />
          </Grid>
        </Grid>
        <Grid container direction='row' alignItems='center' sx={{ mt: 1 }}>
          <Grid item>
            <Typography sx={{ color: theme.palette.sidebarContent.labelText }}>
                Expected AP Count
            </Typography>
          </Grid>
          <Grid item xs={6} sx={{ marginLeft: 'auto' }}>
            {isHSPrefix && (
              <Typography align={'right'} sx={{ color: theme.palette.sidebarContent.valueText, marginLeft: 'auto', my: 1 }}>
                  {expectedAPCount ?? 0}
              </Typography>
            )}
            {!isHSPrefix && (
              /* Input  */
              <TextField 
                select
                onChange={handleExpectedAPCountChanged}
                disabled={props.waitingOnSettings}
                value={expectedAPCount ?? 0}
                fullWidth
                sx={{ 
                  "& .MuiOutlinedInput-root":{"& > fieldset": {borderColor: theme.palette.secondary.main}},
                  "& .MuiOutlinedInput-input":{color: 'white'},
                  "& .MuiOutlinedInput-input.Mui-disabled":{WebkitTextFillColor: 'white'},
                  "& .MuiSelect-icon":{color: 'white'},
                  "& .Mui-disabled":{"& > .MuiSelect-icon": {color: 'inherit'}},
                  textAlignLast: 'end'
                }}
              >
                {[0, 1, 2, 3, 4].map((op) => { 
                  return (
                    <MenuItem key={op} value={op}>
                      {op}
                    </MenuItem>
                  )
                })}
              </TextField>
            )}
          </Grid>
        </Grid>
      </Grid>
      { props.cloudSystemDetails.isDashboardEnabled && (
        <Grid container>
          <Grid container direction='row' alignItems='center' sx={{ mt: 1 }}>
            <Grid item>
              <Typography sx={{ color: theme.palette.sidebarContent.labelText }}>
                  Operating Timezone
              </Typography>
            </Grid>
            <Grid item xs={6} sx={{ marginLeft: 'auto' }}>
              {/* Input  */}
              <OperatingTimezoneSelect 
                timezone={ props.currentSettingsInputs?.timezone ?? generalSettings.timezone} 
                onChange={handleGeneralSettingsUpdated} 
                disabled={props.waitingOnSettings}
              />
            </Grid>
          </Grid>
          <Grid container direction='row' alignItems='center' sx={{ mt: 1 }}>
            <Grid item>
              <Typography sx={{ color: theme.palette.sidebarContent.labelText }}>
                  Software Upd. Frequency
              </Typography>
            </Grid>
            <Grid item xs={6} sx={{ marginLeft: 'auto' }}>
              {/* Input  */}
              <SoftwareUpdFrequencySelect 
                updateFrequency={props.currentSettingsInputs?.updateFrequency ?? generalSettings.updateFrequency} 
                onChange={handleGeneralSettingsUpdated} 
                disabled={props.waitingOnSettings}
              />
            </Grid>
          </Grid>
          <Grid container direction='row' alignItems='center' sx={{ mt: 1 }}>
            <Grid item>
              <Typography sx={{ color: theme.palette.sidebarContent.labelText }}>
                  Content Load Time
              </Typography>
            </Grid>
            <Grid item xs={6} sx={{ marginLeft: 'auto' }}>
              {/* Input  */}
              <ContentLoadSelect 
                loadTime={props.currentSettingsInputs?.loadTime ?? generalSettings.loadTime} 
                onChange={handleGeneralSettingsUpdated} 
                disabled={props.waitingOnSettings}
              />
            </Grid>
          </Grid>
          <Grid container direction='row' alignItems='center' sx={{ mt: 1 }}>
            <Grid item>
              <Typography sx={{ color: theme.palette.sidebarContent.labelText }}>
                  Operating Region
              </Typography>
            </Grid>
            <Grid item xs={6} sx={{ marginLeft: 'auto' }}>
              {/* Input  */}
              <OperatingRegionSelect 
                wifiRegion={props.currentSettingsInputs?.wifiRegion ?? generalSettings.wifiRegion} 
                onChange={handleGeneralSettingsUpdated} 
                disabled={props.waitingOnSettings}
              />
            </Grid>
          </Grid>
          <Grid container direction='row' alignItems='center' sx={{ mt: 1 }}>
            <Grid item>
              <Typography sx={{ color: theme.palette.sidebarContent.labelText }}>
                  WiFi Frequency
              </Typography>
            </Grid>
            <Grid item xs={6} sx={{ marginLeft: 'auto' }}>
              {/* Input  */}
              <WiFiFrequencySelect 
                wifiFrequency={props.currentSettingsInputs?.wifiFrequency ?? generalSettings.wifiFrequency} 
                onChange={handleGeneralSettingsUpdated} 
                disabled={props.waitingOnSettings}
              />
            </Grid>
          </Grid>
          <Grid container direction='row' alignItems='center' sx={{ mt: 1 }}>
            <Grid item>
              <Typography sx={{ color: theme.palette.sidebarContent.labelText }}>
                  Status WiFi Channel
              </Typography>
            </Grid>
            <Grid item xs={6} sx={{ marginLeft: 'auto' }}>
              {/* Input  */}
              <StatusWiFiChannelSelect 
                wifiRegion={props.currentSettingsInputs?.wifiRegion ?? generalSettings.wifiRegion} 
                wifiFrequency={props.currentSettingsInputs?.wifiFrequency ?? generalSettings.wifiFrequency} 
                wifiChannel={props.currentSettingsInputs?.wifiChannel ?? generalSettings.wifiChannel} 
                onChange={handleGeneralSettingsUpdated} 
                disabled={props.waitingOnSettings}
              />
            </Grid>
          </Grid>
          <Grid container direction='row' alignItems='center' sx={{ mt: 1 }}>
            <Grid item>
              <Typography sx={{ color: theme.palette.sidebarContent.labelText }}>
                  Status SSID
              </Typography>
            </Grid>
            <Grid item xs={6} sx={{ marginLeft: 'auto' }}>
              {/* Input  */}
              <TextField 
                onChange={handleWifiSSIDChanged}
                disabled={props.waitingOnSettings}
                value={props.currentSettingsInputs?.wifiSSID ?? generalSettings.wifiSSID}
                fullWidth
                sx={{ 
                  "& .MuiOutlinedInput-root":{"& > fieldset": {borderColor: theme.palette.secondary.main}},
                  "& .MuiOutlinedInput-input.Mui-disabled":{WebkitTextFillColor: 'white'},
                  input: { 
                    color: theme.palette.sidebarContent.valueText, 
                  }, 
                  textAlignLast: 'end'
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      )}
      { props.cloudSystemDetails.isDashboardEnabled && (
        <Grid container sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.secondary.main}` }}>
          <Grid container direction='row' alignItems='center'>
            <Grid item>
              <Typography sx={{ color: theme.palette.sidebarContent.labelText }}>
                  Location
              </Typography>
            </Grid>
            <Grid item xs={6} sx={{ marginLeft: 'auto' }}>
              {/* Input  */}
              <TextField 
                select
                onChange={handleLocationTypeChanged}
                disabled={props.waitingOnSettings}
                value={props.currentSettingsInputs?.install_type ?? generalSettings.install_type}
                fullWidth
                sx={{ 
                  "& .MuiOutlinedInput-root":{"& > fieldset": {borderColor: theme.palette.secondary.main}},
                  "& .MuiOutlinedInput-input":{color: 'white'},
                  "& .MuiOutlinedInput-input.Mui-disabled":{WebkitTextFillColor: 'white'},
                  "& .MuiSelect-icon":{color: 'white'},
                  "& .Mui-disabled":{"& > .MuiSelect-icon": {color: 'inherit'}},
                  textAlignLast: 'end'
                }}
              >
                <MenuItem key={'region'} value={'region'}>By Region</MenuItem>
                <MenuItem key={'airport'} value={'airport'}>By Airport Code</MenuItem>
              </TextField>
            </Grid>
          </Grid>
          {/* Location By Region */}
          {(props.currentSettingsInputs?.install_type ?? generalSettings.install_type) === "region" && (
            <>
              <Grid container direction='row' alignItems='center' sx={{ mt: 1 }}>
                <Grid item>
                  <Typography sx={{ color: theme.palette.sidebarContent.labelText }}>
                      Country
                  </Typography>
                </Grid>
                <Grid item xs={6} sx={{ marginLeft: 'auto' }}>
                  {/* Input  */}
                  <CountrySelect 
                    install_country={props.currentSettingsInputs?.install_country ?? generalSettings.install_country} 
                    onChange={handleGeneralSettingsUpdated} 
                    disabled={props.waitingOnSettings}
                  />
                </Grid>
              </Grid>
              {/* Only show State select if install_country is US */}
              {(props.currentSettingsInputs?.install_country ?? generalSettings.install_country) === 'US' && (<Grid container direction='row' alignItems='center' sx={{ mt: 1 }}>
                <Grid item>
                  <Typography sx={{ color: theme.palette.sidebarContent.labelText }}>
                      State
                  </Typography>
                </Grid>
                <Grid item xs={6} sx={{ marginLeft: 'auto' }}>
                  {/* Input  */}
                  <StateSelect 
                    install_state={props.currentSettingsInputs?.install_state ?? generalSettings.install_state} 
                    onChange={handleGeneralSettingsUpdated} 
                    disabled={props.waitingOnSettings}
                  />
                </Grid>
              </Grid>)}
              <Grid container direction='row' alignItems='center' sx={{ mt: 1 }}>
                <Grid item>
                  <Typography sx={{ color: theme.palette.sidebarContent.labelText }}>
                      City
                  </Typography>
                </Grid>
                <Grid item xs={6} sx={{ marginLeft: 'auto' }}>
                  {/* Input  */}
                  <TextField 
                    onChange={handleCityChanged}
                    disabled={props.waitingOnSettings}
                    value={props.currentSettingsInputs?.install_city ?? generalSettings.install_city}
                    fullWidth
                    sx={{ 
                      "& .MuiOutlinedInput-root":{"& > fieldset": {borderColor: theme.palette.secondary.main}},
                      "& .MuiOutlinedInput-input.Mui-disabled":{WebkitTextFillColor: 'white'},
                      input: { 
                        color: theme.palette.sidebarContent.valueText, 
                      }, 
                      textAlignLast: 'end'
                    }}
                  />
                </Grid>
              </Grid>
            </>
          )}
          {/* Location By Airport Code */}
          {(props.currentSettingsInputs?.install_type ?? generalSettings.install_type) === "airport" && (
            <>
              <Grid container direction='row' alignItems='center' sx={{ mt: 1 }}>
                <Grid item>
                  <Typography sx={{ color: theme.palette.sidebarContent.labelText }}>
                      Airport Code
                  </Typography>
                </Grid>
                <Grid item xs={6} sx={{ marginLeft: 'auto' }}>
                  {/* Input  */}
                  <TextField 
                    onChange={handleAirportCodeChanged}
                    disabled={props.waitingOnSettings}
                    value={props.currentSettingsInputs?.airport_id ?? generalSettings.airport_id}
                    fullWidth
                    sx={{ 
                      "& .MuiOutlinedInput-root":{"& > fieldset": {borderColor: theme.palette.secondary.main}},
                      "& .MuiOutlinedInput-input.Mui-disabled":{WebkitTextFillColor: 'white'},
                      input: { 
                        color: theme.palette.sidebarContent.valueText, 
                      }, 
                      textAlignLast: 'end'
                    }}
                  />
                </Grid>
              </Grid>
              {/* Panel showing computed values */}
              <Grid container item sx={{ backgroundColor: theme.palette.sidebarContent.main, p: 2, my: 2 }}>
                <Grid container item direction={'row'}>
                  <Grid item>
                    <Typography sx={{ color: theme.palette.sidebarContent.labelText }}>
                      Latitude
                    </Typography>
                  </Grid>
                  <Grid sx={{ marginLeft: 'auto' }}>
                    <Typography sx={{ color: theme.palette.sidebarContent.valueText }}>
                      {airportDetails?.latitude ?? "-- --"}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container item direction={'row'}>
                  <Grid item>
                    <Typography sx={{ color: theme.palette.sidebarContent.labelText }}>
                      Longitude
                    </Typography>
                  </Grid>
                  <Grid sx={{ marginLeft: 'auto' }}>
                    <Typography sx={{ color: theme.palette.sidebarContent.valueText }}>
                      {airportDetails?.longitude ?? "-- --"}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container item direction={'row'}>
                  <Grid item>
                    <Typography sx={{ color: theme.palette.sidebarContent.labelText }}>
                      Name
                    </Typography>
                  </Grid>
                  <Grid sx={{ marginLeft: 'auto' }}>
                    <Typography sx={{ color: theme.palette.sidebarContent.valueText }}>
                      {airportDetails?.name ?? "-- --"}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container item direction={'row'}>
                  <Grid item>
                    <Typography sx={{ color: theme.palette.sidebarContent.labelText }}>
                      Region
                    </Typography>
                  </Grid>
                  <Grid sx={{ marginLeft: 'auto' }}>
                    <Typography sx={{ color: theme.palette.sidebarContent.valueText }}>
                      {airportDetails?.region ?? "-- --"}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </>
          )}
        </Grid>
      )}
    </Paper>
  );
};

export default SideBarSettings;